import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface TestimonialsSectionProps {
  bookSessionHref?: string;
  variant?: 'images' | 'text';
  theme?: 'dark' | 'light';
}

const textTestimonials = [
  {
    text: "As a therapist, I regularly recommend Yulia to clients because of the safe, compassionate, creative space she provides. She meets people exactly where they are in their emotional journey.",
    attribution: "— Jordan Scott, Therapist",
  },
  {
    text: "After a thoughtful conversation, Yulia picked an exercise that was exactly what I needed. Already during it I started to feel lighter — by the end, something had unclenched inside, relaxed, flowed.",
    attribution: "— Viktoria Yudina",
  },
  {
    text: "Working with Yulia has been a truly meaningful experience. She creates a calm, safe, non-judgmental space, and I gained many valuable insights into myself.",
    attribution: "— Katja Hoza",
  },
];

export default function TestimonialsSection({ bookSessionHref, variant = 'images', theme = 'dark' }: TestimonialsSectionProps) {
  const navigate = useNavigate();

  const isLight = theme === 'light';
  const c = {
    sectionBg: isLight ? '#DBCEC5' : '#829BB8',
    heading: isLight ? '#385B47' : '#385B47',
    textPrimary: isLight ? 'rgba(56, 91, 71, 0.9)' : 'rgba(56, 91, 71, 0.9)',
    textSecondary: isLight ? 'rgba(56, 91, 71, 0.85)' : 'rgba(56, 91, 71, 0.85)',
    textMuted: isLight ? 'rgba(56, 91, 71, 0.8)' : 'rgba(56, 91, 71, 0.8)',
    slideBg: isLight ? '#DBCEC5' : '#81998C',
    arrowBg: isLight ? 'rgba(219, 206, 197, 0.5)' : 'rgba(56, 91, 71, 0.15)',
    arrowBorder: isLight ? 'rgba(56, 91, 71, 0.3)' : 'rgba(56, 91, 71, 0.3)',
    arrowIcon: isLight ? '#385B47' : '#385B47',
    dotInactive: isLight ? 'rgba(56, 91, 71, 0.4)' : 'rgba(56, 91, 71, 0.4)',
    badgeBg: isLight ? 'rgba(56, 91, 71, 0.08)' : 'rgba(56, 91, 71, 0.08)',
    badgeBorder: isLight ? 'rgba(56, 91, 71, 0.15)' : 'rgba(56, 91, 71, 0.15)',
    badgeText: isLight ? 'rgba(56, 91, 71, 0.85)' : 'rgba(56, 91, 71, 0.85)',
  };

  const testimonialImages = [
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/c2a1a8ae-2acc-476b-bd66-ada8bc2aef3f_compressed_IMG_0724.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/1beb399f-3358-4725-b2cb-34ddffe60508_compressed_IMG_0725.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/9660001b-4ec8-49f1-b30e-9e6af5bdd537_compressed_IMG_0726.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/b75f30ae-d270-4ce9-9215-5737f96f06a9_compressed_IMG_0727.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/38237103-d018-46a8-8216-e8f6f6c06db4_compressed_IMG_0729.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/c3b326c6-08f6-4ec2-8a90-63063238ea53_compressed_IMG_0730.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/614436ee-dfa0-496b-89b1-0a4a2f8159e8_compressed_IMG_4467_blurred.webp',
    'https://storage.helloreaddy.io/project_files/b4f3eeda-f893-4987-8921-4636c63c4dc6/e35c4ace-d526-4760-a9dd-80287e53d443_compressed_IMG_4468_blurred_v3.webp'
  ];

  const testimonialAlts = [
    'Client testimonial about an art therapy workshop in Chiang Mai',
    'Client testimonial about online art therapy for creative self-expression',
    'Client testimonial about completing an online art therapy course',
    'Client testimonial about art therapy for anxiety and stress relief',
    'Client testimonial about an online art therapy session',
    'Client testimonial about learning neurographica art therapy technique',
    'Handwritten thank-you note from an online art therapy client',
    'Handwritten note of gratitude for art therapy sessions'
  ];

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);
  const [mobileSlideIndex, setMobileSlideIndex] = useState(0);
  const [desktopCarouselIndex, setDesktopCarouselIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isDesktopAutoPlaying, setIsDesktopAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const lightboxRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);
  const desktopAutoPlayRef = useRef<NodeJS.Timeout | null>(null);

  // Mobile slider autoplay
  useEffect(() => {
    if (variant !== 'images') return;

    if (isAutoPlaying) {
      autoPlayRef.current = setInterval(() => {
        setMobileSlideIndex((prev) => (prev + 1) % testimonialImages.length);
      }, 2000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, testimonialImages.length, variant]);

  // Desktop carousel autoplay
  useEffect(() => {
    if (variant !== 'images') return;

    if (isDesktopAutoPlaying) {
      desktopAutoPlayRef.current = setInterval(() => {
        setDesktopCarouselIndex((prev) => {
          const maxIndex = testimonialImages.length - 3;
          return prev >= maxIndex ? 0 : prev + 1;
        });
      }, 2000);
    }

    return () => {
      if (desktopAutoPlayRef.current) {
        clearInterval(desktopAutoPlayRef.current);
      }
    };
  }, [isDesktopAutoPlaying, testimonialImages.length, variant]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoPlaying(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      setMobileSlideIndex((prev) => (prev + 1) % testimonialImages.length);
    }

    if (touchStart - touchEnd < -75) {
      setMobileSlideIndex((prev) => (prev - 1 + testimonialImages.length) % testimonialImages.length);
    }

    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const goToMobileSlide = (index: number) => {
    setMobileSlideIndex(index);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const nextMobileSlide = () => {
    setMobileSlideIndex((prev) => (prev + 1) % testimonialImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const prevMobileSlide = () => {
    setMobileSlideIndex((prev) => (prev - 1 + testimonialImages.length) % testimonialImages.length);
    setIsAutoPlaying(false);
    setTimeout(() => {
      setIsAutoPlaying(true);
    }, 3000);
  };

  const nextDesktopSlide = () => {
    setDesktopCarouselIndex((prev) => {
      const maxIndex = testimonialImages.length - 3;
      return prev >= maxIndex ? 0 : prev + 1;
    });
    setIsDesktopAutoPlaying(false);
    setTimeout(() => {
      setIsDesktopAutoPlaying(true);
    }, 3000);
  };

  const prevDesktopSlide = () => {
    setDesktopCarouselIndex((prev) => {
      const maxIndex = testimonialImages.length - 3;
      return prev <= 0 ? maxIndex : prev - 1;
    });
    setIsDesktopAutoPlaying(false);
    setTimeout(() => {
      setIsDesktopAutoPlaying(true);
    }, 3000);
  };

  const openLightbox = (index: number) => {
    setCurrentLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'unset';
  };

  const nextLightboxImage = () => {
    setCurrentLightboxIndex((prev) => 
      prev === testimonialImages.length - 1 ? 0 : prev + 1
    );
  };

  const prevLightboxImage = () => {
    setCurrentLightboxIndex((prev) => 
      prev === 0 ? testimonialImages.length - 1 : prev - 1
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeLightbox();
    if (e.key === 'ArrowRight') nextLightboxImage();
    if (e.key === 'ArrowLeft') prevLightboxImage();
  };

  const handleBookSession = () => {
    if (bookSessionHref) {
      navigate(bookSessionHref);
      return;
    }
    const element = document.getElementById('services-pricing');
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const isTextVariant = variant === 'text';
  const isImagesVariant = variant === 'images';

  return (
    <>
      <section 
        id="testimonials" 
        className="pt-8 pb-8 relative overflow-hidden"
        style={{ backgroundColor: c.sectionBg }}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          {/* Header */}
          <div className="text-center mb-4">
            <h2 
              className="text-4xl lg:text-5xl font-normal mb-4"
              style={{ 
                letterSpacing: '0.02em',
                lineHeight: '1.3',
                color: c.heading,
                textShadow: 'none'
              }}
            >
              {isTextVariant ? 'What Clients & Colleagues Say' : 'What My Clients Say'}
            </h2>
            
            {/* Google Rating Badge */}
            <a
              href="https://search.google.com/local/reviews?placeid=ChIJETsWOf432jAYVdXmGFJrZA"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mb-4 px-4 py-1.5 rounded-full transition-all duration-300 hover:scale-105 cursor-pointer"
              style={{
                backgroundColor: '#FFFFFF',
                border: `1px solid ${c.badgeBorder}`
              }}
            >
              <span 
                className="text-sm tracking-wide"
                style={{ color: '#D6794E' }}
              >
                ★★★★★
              </span>
              <span 
                className="text-sm font-normal"
                style={{ color: c.badgeText }}
              >
                5.0
              </span>
              <span 
                className="text-sm"
                style={{ color: c.badgeText }}
              >
                (7 reviews)
              </span>
            </a>

            {/* Read on Google link */}
            <div className="mb-4">
              <a
                href="https://www.google.com/maps/place/Yuvati+Art+~+Art+Therapy+%26+Counselling/@18.7134864,98.9188344,17z/data=!4m6!3m5!1s0x30da37fe39163b11:0x646b5218e6d55518!8m2!3d18.7134864!4d98.9188344!16s%2Fg%2F11nr3gtfhg"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-normal underline cursor-pointer transition-opacity duration-300 hover:opacity-75 whitespace-nowrap"
                style={{ color: '#FFFFFF' }}
              >
                Read on Google ↗
              </a>
            </div>
            
            <p 
              className="text-xl max-w-3xl mx-auto mb-4"
              style={{ 
                color: c.textSecondary,
                lineHeight: '1.6'
              }}
            >
              Stories of change through creative expression
            </p>
          </div>

          {/* TEXT VARIANT: 3 testimonial cards */}
          {isTextVariant && (
            <div className="max-w-6xl mx-auto mb-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
                {textTestimonials.map((t, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl p-6 lg:p-8 flex flex-col transition-all duration-300 hover:scale-[1.02] bg-[#385B47] border border-[#3D5C4A] ${i > 0 ? 'hidden md:flex' : ''}`}
                  >
                    {/* Large quotation mark */}
                    <div className="mb-4">
                      <i
                        className="ri-double-quotes-l text-4xl lg:text-5xl leading-none block"
                        style={{ color: '#D6794E' }}
                      ></i>
                    </div>

                    {/* Testimonial text */}
                    <p
                      className="text-base lg:text-lg leading-relaxed flex-1 mb-5"
                      style={{
                        color: 'rgba(251, 247, 240, 0.9)',
                        lineHeight: '1.75',
                      }}
                    >
                      {t.text}
                    </p>

                    {/* Attribution */}
                    <p
                      className="text-sm italic"
                      style={{
                        color: '#D6794E',
                      }}
                    >
                      {t.attribution}
                    </p>
                  </div>
                ))}
              </div>

              {/* See more reviews link */}
              <div className="text-center mt-8 flex items-center justify-center">
                <a
                  href="/testimonials"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/testimonials');
                  }}
                  className="inline-flex items-center gap-2 text-sm font-normal underline cursor-pointer transition-opacity duration-300 hover:opacity-75 whitespace-nowrap"
                  style={{ color: '#FFFFFF' }}
                >
                  See more reviews
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          )}

          {/* IMAGES VARIANT: original carousel (unchanged) */}
          {isImagesVariant && (
            <>
            {/* Mobile Fullscreen Slider - Only visible on mobile */}
            <div className="lg:hidden relative w-full mb-4">
              <div 
                className="relative overflow-hidden -mx-6"
                style={{
                  height: 'calc(100vh - 200px)',
                  minHeight: '500px',
                  maxHeight: '800px'
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <div 
                  className="flex h-full transition-transform duration-500 ease-out"
                  style={{ transform: `translateX(-${mobileSlideIndex * 100}%)` }}
                >
                  {testimonialImages.map((image, index) => (
                    <div
                      key={index}
                      className="min-w-full flex-shrink-0 h-full flex items-center justify-center"
                      style={{
                        backgroundColor: c.slideBg
                      }}
                    >
                      <img
                        src={image}
                        alt={testimonialAlts[index]}
                        className="max-w-full max-h-full w-auto h-auto cursor-pointer"
                        style={{
                          objectFit: 'contain',
                          objectPosition: 'center'
                        }}
                        onClick={() => openLightbox(index)}
                      />
                    </div>
                  ))}
                </div>

                {/* Navigation Arrows - Floating on top of image */}
                <button
                  onClick={prevMobileSlide}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
                  style={{ 
                    backgroundColor: c.arrowBg,
                    border: `1px solid ${c.arrowBorder}`,
                    backdropFilter: 'blur(10px)'
                  }}
                  aria-label="Previous testimonial"
                >
                  <i 
                    className="ri-arrow-left-line text-xl"
                    style={{ color: c.arrowIcon }}
                  ></i>
                </button>
                
                <button
                  onClick={nextMobileSlide}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
                  style={{ 
                    backgroundColor: c.arrowBg,
                    border: `1px solid ${c.arrowBorder}`,
                    backdropFilter: 'blur(10px)'
                  }}
                  aria-label="Next testimonial"
                >
                  <i 
                    className="ri-arrow-right-line text-xl"
                    style={{ color: c.arrowIcon }}
                  ></i>
                </button>
              </div>

              {/* Dots Navigation */}
              <div className="flex justify-center gap-2 mt-6">
                {testimonialImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => goToMobileSlide(index)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                      index === mobileSlideIndex 
                        ? 'scale-125' 
                        : 'hover:scale-110'
                    }`}
                    style={{
                      backgroundColor: index === mobileSlideIndex ? '#D6794E' : c.dotInactive,
                      boxShadow: index === mobileSlideIndex 
                        ? 'none' 
                        : 'none'
                    }}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Desktop Horizontal Carousel - Only visible on desktop */}
            <div className="hidden lg:block max-w-6xl mx-auto mb-10 relative">
              {/* Navigation Arrows */}
              <button
                onClick={prevDesktopSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-16 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
                style={{ 
                  backgroundColor: c.arrowBg,
                  border: `1px solid ${c.arrowBorder}`,
                  backdropFilter: 'blur(10px)'
                }}
                aria-label="Previous testimonials"
              >
                <i 
                  className="ri-arrow-left-line text-2xl"
                  style={{ color: c.arrowIcon }}
                ></i>
              </button>
              
              <button
                onClick={nextDesktopSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-16 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer z-20"
                style={{ 
                  backgroundColor: c.arrowBg,
                  border: `1px solid ${c.arrowBorder}`,
                  backdropFilter: 'blur(10px)'
                }}
                aria-label="Next testimonials"
              >
                <i 
                  className="ri-arrow-right-line text-2xl"
                  style={{ color: c.arrowIcon }}
                ></i>
              </button>

              {/* Carousel Container */}
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-500 ease-out gap-6 lg:gap-8"
                  style={{ 
                    transform: `translateX(-${desktopCarouselIndex * (100 / 3 + 2.67)}%)` 
                  }}
                >
                  {testimonialImages.map((image, index) => (
                    <div
                      key={index}
                      className="group cursor-pointer relative flex-shrink-0"
                      style={{ width: 'calc(33.333% - 1.33rem)' }}
                      onClick={() => openLightbox(index)}
                    >
                      <div 
                        className="relative rounded-lg overflow-hidden transition-all duration-500 group-hover:scale-[1.03]"
                        style={{
                          backgroundColor: c.slideBg,
                          boxShadow: isLight ? '0 4px 20px rgba(0, 0, 0, 0.15)' : '0 4px 20px rgba(0, 0, 0, 0.4)'
                        }}
                      >
                        <img
                          src={image}
                          alt={testimonialAlts[index]}
                          className="w-full h-auto object-contain"
                          style={{ 
                            minHeight: '300px',
                            maxHeight: '400px'
                          }}
                        />
                        
                        {/* Subtle hover overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            </>
          )}

          {/* CTA Section */}
          <div className="text-center">
            <p 
              className="text-lg mb-4"
              style={{ 
                color: c.textSecondary,
                lineHeight: '1.6'
              }}
            >
              Want to experience it for yourself?
            </p>
            <button 
              className="px-10 py-4 rounded-xl font-normal text-white transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer group relative overflow-hidden bg-[#D6794E] hover:bg-[#385B47]"
              style={{ 
                textShadow: '0 1px 2px rgba(0,0,0,0.3)'
              }}
              onClick={handleBookSession}
            >
              <span className="relative z-10">Book Your Session</span>
            </button>
          </div>
        </div>

        {/* Full-screen Lightbox */}
        {lightboxOpen && (
          <div 
            ref={lightboxRef}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{ backgroundColor: 'rgba(56, 91, 71, 0.95)' }}
            onClick={closeLightbox}
            onKeyDown={handleKeyDown}
            tabIndex={-1}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 z-60 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <i 
                className="ri-close-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>

            {/* Navigation arrows */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                prevLightboxImage();
              }}
              className="absolute left-6 top-1/2 -translate-y-1/2 z-60 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <i 
                className="ri-arrow-left-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                nextLightboxImage();
              }}
              className="absolute right-6 top-1/2 -translate-y-1/2 z-60 w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{ 
                backgroundColor: 'rgba(245, 243, 239, 0.1)',
                border: '1px solid rgba(245, 243, 239, 0.3)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <i 
                className="ri-arrow-right-line text-2xl"
                style={{ color: '#F5F3EF' }}
              ></i>
            </button>

            {/* Image container */}
            <div 
              className="max-w-5xl max-h-[90vh] mx-auto px-20"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={testimonialImages[currentLightboxIndex]}
                alt={testimonialAlts[currentLightboxIndex]}
                className="w-full h-auto object-contain rounded-lg"
                style={{
                  maxHeight: '90vh',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)'
                }}
              />
              
              {/* Image info */}
              <div className="text-center mt-6">
                <p 
                  className="text-sm"
                  style={{ 
                    color: 'rgba(251, 247, 240, 0.85)'
                  }}
                >
                  From Instagram • {currentLightboxIndex + 1} of {testimonialImages.length}
                </p>
              </div>
            </div>

            {/* Dots indicator */}
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-3">
              {testimonialImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentLightboxIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-300 cursor-pointer ${
                    index === currentLightboxIndex 
                      ? 'scale-125' 
                      : 'hover:scale-110'
                  }`}
                  style={{
                    backgroundColor: index === currentLightboxIndex ? '#D6794E' : 'rgba(245, 243, 239, 0.4)',
                    boxShadow: index === currentLightboxIndex 
                      ? 'none' 
                      : 'none'
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}
